import type { Answers, PersonalisationField, Category } from './types';

export interface Option {
  id: string;
  label: string;
}

export type QuestionKey = Exclude<keyof Answers, 'songTitle' | 'personalisation'>;

export interface StepDef {
  id: QuestionKey | 'songTitle';
  phase: 'Your situation' | 'Your first fix' | 'Your plan';
  heading: string;
  helper?: string;
  kind: 'multi' | 'text';
  /** Options that contradict the rest, so they clear the other picks. */
  exclusiveOptions?: string[];
  options?: Option[];
}

const MULTI_HELPER = 'Choose as many as apply.';

export const goalOptions: Option[] = [
  { id: 'FIRST_LISTENERS', label: 'Getting my first people genuinely interested in the music.' },
  { id: 'MORE_DISCOVERY', label: 'Reaching new listeners beyond the people who already know me.' },
  { id: 'NEXT_RELEASE', label: 'Giving an upcoming release a clearer plan.' },
  { id: 'MORE_RETURN', label: 'Getting interested listeners to come back.' },
  { id: 'LOCAL_SHOW', label: 'Building interest in a particular live show.' },
];

export const readinessOptions: Option[] = [
  { id: 'RELEASED', label: 'I have a released song to focus on.' },
  { id: 'UNRELEASED_SHAREABLE', label: 'I have an unreleased song or excerpt I can share.' },
  { id: 'CHOOSE_SONG', label: "I have music, but haven't chosen one song for this." },
  { id: 'NO_SHAREABLE', label: "I don't have a shareable song or excerpt yet." },
];

export const steps: StepDef[] = [
  {
    id: 'goal',
    phase: 'Your situation',
    heading: 'What are you working on right now?',
    helper: MULTI_HELPER,
    kind: 'multi',
    options: goalOptions,
  },
  {
    id: 'readiness',
    phase: 'Your situation',
    heading: 'Where is the music?',
    helper: MULTI_HELPER,
    kind: 'multi',
    exclusiveOptions: ['NO_SHAREABLE'],
    options: readinessOptions,
  },
  {
    id: 'songTitle',
    phase: 'Your situation',
    heading: 'Is there a song or project this is about?',
    helper: "Optional. Leave it blank if you'd rather not say.",
    kind: 'text',
  },
  {
    id: 'audience',
    phase: 'Your situation',
    heading: 'Who are you currently trying to reach?',
    helper: MULTI_HELPER,
    kind: 'multi',
    exclusiveOptions: ['UNSURE'],
    options: [
      { id: 'SPECIFIC_REASON', label: 'A specific kind of listener, and I can explain why this song fits.' },
      { id: 'REFERENCE_NAMES', label: "Fans of particular artists or a broad genre, but I haven't narrowed it down further." },
      { id: 'TOPIC_FIRST', label: "People who relate to the subject of the song, but I'm less clear about the musical fit." },
      { id: 'EVERYONE', label: 'Anyone who might like it.' },
      { id: 'UNSURE', label: "I'm not sure yet." },
    ],
  },
  {
    id: 'discovery',
    phase: 'Your situation',
    heading: "How do people who don't already know you usually find your music?",
    helper: MULTI_HELPER,
    kind: 'multi',
    exclusiveOptions: ['NOT_STARTED', 'UNKNOWN'],
    options: [
      { id: 'OWN_CONTENT', label: 'My own song-related posts or videos.' },
      { id: 'LIVE', label: 'Live performances or events.' },
      { id: 'COLLAB', label: 'Other artists, creators or collaborations.' },
      { id: 'CURATION', label: 'Playlists, radio, curators or similar introductions.' },
      { id: 'ADS', label: 'Paid promotion.' },
      { id: 'EXISTING_CIRCLE', label: 'Mostly friends, existing followers or people I already know.' },
      { id: 'SCATTERED', label: "A mixture, but I couldn't say which route is helping." },
      { id: 'NOT_STARTED', label: "I haven't deliberately tried reaching new people yet." },
      { id: 'UNKNOWN', label: "I don't know where new listeners come from." },
    ],
  },
  {
    id: 'introduction',
    phase: 'Your first fix',
    heading: 'What have you mainly put in front of people recently?',
    helper: MULTI_HELPER,
    kind: 'multi',
    exclusiveOptions: ['VERY_LITTLE', 'NOT_ONLINE'],
    options: [
      { id: 'ANNOUNCEMENTS', label: "Artwork, release dates and 'out now' announcements." },
      { id: 'SONG_LED', label: 'Clips where the music is the main thing people hear.' },
      { id: 'SONG_CONTEXT', label: 'Stories or context that lead into the song.' },
      { id: 'OTHER_CONTENT', label: 'Content people enjoy, but which is mostly separate from my music.' },
      { id: 'FULL_MUSIC', label: 'Full songs or longer performances.' },
      { id: 'VERY_LITTLE', label: 'Very little recently.' },
      { id: 'NOT_ONLINE', label: "Online content isn't currently my main route." },
    ],
  },
  {
    id: 'response',
    phase: 'Your first fix',
    heading: 'In the last 30 days, which of these have you actually noticed?',
    helper: "Select what you've seen, not what you think should be happening. These can overlap.",
    kind: 'multi',
    exclusiveOptions: ['NO_OBSERVED_RESPONSE', 'NO_RECENT_ATTEMPT', 'UNKNOWN'],
    options: [
      { id: 'LIGHT_ATTENTION', label: 'Views or likes, without an obvious response about the music.' },
      { id: 'SONG_ENQUIRIES', label: 'People asking about the song or where to hear it.' },
      { id: 'DELIBERATE_INTEREST', label: 'People telling me they listened, or other specific signs they chose the music.' },
      { id: 'RETURNING_INTEREST', label: 'Some of the same people coming back for more music or another show.' },
      { id: 'NO_OBSERVED_RESPONSE', label: "I've shared music but haven't noticed much response." },
      { id: 'NO_RECENT_ATTEMPT', label: "I haven't shared enough recently to have a useful answer." },
      { id: 'UNKNOWN', label: "I can't tell yet." },
    ],
  },
  {
    id: 'nextStep',
    phase: 'Your first fix',
    heading: 'When someone wants more, what is the usual next step you give them?',
    helper: MULTI_HELPER,
    kind: 'multi',
    exclusiveOptions: ['UNKNOWN'],
    options: [
      { id: 'CLEAR_DESTINATION', label: 'One clear action, with an easy route to the featured song or relevant destination.' },
      { id: 'GENERAL_PROFILE', label: 'My general profile or a general link page.' },
      { id: 'MANY_ACTIONS', label: 'Several requests, such as listening, following, sharing and signing up.' },
      { id: 'NO_ROUTE', label: "There isn't a clear next step yet." },
      { id: 'UNRELEASED_UNCLEAR', label: "The song isn't out, and I'm unsure what to ask people to do." },
      { id: 'UNKNOWN', label: "I'm not sure what someone sees or where they end up." },
    ],
  },
  {
    id: 'return',
    phase: 'Your first fix',
    heading: 'After your first round of promotion, what usually happens?',
    helper: MULTI_HELPER,
    kind: 'multi',
    exclusiveOptions: ['NOT_YET'],
    options: [
      { id: 'RELATED_FOLLOW_UP', label: 'I give people another useful encounter with the music or a relevant next song/show.' },
      { id: 'SAME_REQUEST', label: 'I repeat essentially the same request to listen or support it.' },
      { id: 'MOVE_ON', label: 'I normally move on to the next thing.' },
      { id: 'NO_FOLLOW_UP', label: "I don't usually have a follow-up planned." },
      { id: 'NOT_YET', label: "I haven't reached that point yet." },
    ],
  },
  {
    id: 'learning',
    phase: 'Your first fix',
    heading: 'How do you decide what to try again?',
    helper: MULTI_HELPER,
    kind: 'multi',
    exclusiveOptions: ['TOO_EARLY'],
    options: [
      { id: 'COMPARE', label: 'I compare similar attempts and look for interest in the music.' },
      { id: 'REPEAT_FEELING', label: 'I repeat what feels like it went well, without much comparison.' },
      { id: 'CHANGE_EVERYTHING', label: 'I tend to change several things at once.' },
      { id: 'VIEWS_ONLY', label: 'Mostly from views or likes.' },
      { id: 'RANDOM', label: 'I move to another idea without much of a method.' },
      { id: 'TOO_EARLY', label: "I haven't made enough attempts to compare yet." },
    ],
  },
  {
    id: 'capacity',
    phase: 'Your plan',
    heading: 'How much time can you realistically give this each week?',
    helper: 'If it varies, pick the range you can manage in a bad week as well. This limits the size of the plan, it is not a judgement.',
    kind: 'multi',
    options: [
      { id: 'TINY', label: 'Less than 30 minutes.' },
      { id: 'SMALL', label: 'Around 30 to 60 minutes.' },
      { id: 'MEDIUM', label: 'Around 1 to 3 hours.' },
      { id: 'LARGE', label: 'More than 3 hours.' },
    ],
  },
];

export const capacityMinutes: Record<string, number> = {
  TINY: 25,
  SMALL: 45,
  MEDIUM: 90,
  LARGE: 150,
};

export const optionLabel = (stepId: string, optionId: string): string => {
  if (stepId === 'goal') return goalOptions.find((o) => o.id === optionId)?.label ?? optionId;
  if (stepId === 'readiness') return readinessOptions.find((o) => o.id === optionId)?.label ?? optionId;
  const step = steps.find((s) => s.id === stepId);
  return step?.options?.find((o) => o.id === optionId)?.label ?? optionId;
};

// ---- Targeted personalisation (max three fields, chosen by provisional category) ----

export const personalisationFields: Record<Category, PersonalisationField[]> = {
  AUDIENCE: [
    {
      id: 'sound_description',
      label: 'Describe the sound of this song in a sentence or two.',
      helper: 'Instruments, voice, pace or atmosphere are helpful. Instrumental is fine.',
      kind: 'textarea',
    },
    {
      id: 'reference_detail',
      label: 'Optional: name one or two reference songs and say what actually sounds similar.',
      kind: 'textarea',
      optional: true,
    },
    {
      id: 'keep_making',
      label: 'Which part of your music do you most want to keep making?',
      kind: 'textarea',
    },
  ],
  DISCOVERY: [
    {
      id: 'music_description',
      label: 'Briefly describe the music you want to introduce.',
      kind: 'textarea',
    },
    {
      id: 'route_choice',
      label: 'Which route could you realistically try?',
      kind: 'select',
      options: [
        { id: 'PERFORMANCE', label: 'Performance' },
        { id: 'OTHER_CREATOR', label: 'Working with another artist or creator' },
        { id: 'CURATOR', label: 'A relevant curator or community' },
        { id: 'OWN_POSTS', label: 'My own posts' },
        { id: 'UNSURE', label: 'Unsure' },
      ],
    },
    {
      id: 'existing_access',
      label: "Relevant existing access or opportunity: paste one real example, or say 'none yet'.",
      helper: 'For a show route, tell us the city or area and whether the event is confirmed.',
      kind: 'textarea',
    },
  ],
  CONTENT: [
    {
      id: 'first_moment',
      label: 'What would you like people to hear first?',
      helper: 'Paste a short lyric you own, or describe the musical moment. No timestamp needed.',
      kind: 'textarea',
    },
    {
      id: 'format_comfort',
      label: 'What can you comfortably make?',
      kind: 'select',
      options: [
        { id: 'FACE', label: 'Face on camera' },
        { id: 'HANDS', label: 'Hands, instruments or details only' },
        { id: 'ARTWORK', label: 'Existing artwork or footage' },
        { id: 'AUDIO', label: 'Audio only' },
        { id: 'UNSURE', label: 'Unsure' },
      ],
    },
    {
      id: 'recent_caption',
      label: 'Optional: paste a recent caption, or describe how you usually open a post.',
      kind: 'textarea',
      optional: true,
    },
  ],
  NEXT_STEP: [
    {
      id: 'current_cta',
      label: 'Paste the words you currently use to send people towards your music.',
      kind: 'textarea',
    },
    {
      id: 'destination_report',
      label: 'Where does that take them, and is the featured song or event easy to find there?',
      helper: 'Describe it in words. We do not open or inspect links.',
      kind: 'textarea',
    },
    {
      id: 'available_destination',
      label: 'Which destination can you offer right now?',
      kind: 'select',
      options: [
        { id: 'RELEASED_SONG', label: 'A released song' },
        { id: 'EVENT_PAGE', label: 'An existing event page' },
        { id: 'REMINDER', label: 'An existing signup or reminder' },
        { id: 'PROFILE', label: 'A profile' },
        { id: 'NONE', label: 'None yet' },
      ],
    },
  ],
  RETURN: [
    {
      id: 'responded_song',
      label: 'Which song or piece of music have people responded to? Give one brief example of that response.',
      kind: 'textarea',
    },
    {
      id: 'available_next',
      label: 'What could you share next without inventing a new project?',
      kind: 'select',
      options: [
        { id: 'ANOTHER_SECTION', label: 'Another section of the same song' },
        { id: 'DIFFERENT_PERFORMANCE', label: 'A different performance' },
        { id: 'GENUINE_STORY', label: 'A genuine story behind it' },
        { id: 'ANOTHER_SONG', label: 'Another existing song' },
        { id: 'CONFIRMED_EVENT', label: 'A confirmed event' },
        { id: 'NONE', label: 'None of these yet' },
      ],
    },
    {
      id: 'format_preference',
      label: 'Any practical format preference that changes the work?',
      kind: 'select',
      options: [
        { id: 'PERFORMANCE', label: 'Performance' },
        { id: 'NO_FACE', label: 'No face' },
        { id: 'WRITING', label: 'Writing' },
        { id: 'AUDIO_ONLY', label: 'Audio only' },
        { id: 'NO_PREFERENCE', label: 'No preference' },
      ],
    },
  ],
  TESTING: [
    {
      id: 'activity_under_review',
      label: 'Which recent activity are you trying to understand?',
      helper: 'A post, a song excerpt, an outreach approach, a show invitation or another specific attempt.',
      kind: 'textarea',
    },
    {
      id: 'available_observations',
      label: 'Which observations can you actually access?',
      kind: 'select',
      options: [
        { id: 'REPLIES', label: 'Counts of relevant replies' },
        { id: 'CLICKS', label: 'Route clicks' },
        { id: 'POST_DATA', label: 'Comparable post data' },
        { id: 'STREAMING', label: 'Aggregate streaming data' },
        { id: 'NONE', label: 'None yet' },
      ],
    },
    {
      id: 'attempt_pair',
      label: 'Optional: describe two recent attempts and what changed between them.',
      kind: 'textarea',
      optional: true,
    },
  ],
  BASELINE: [
    {
      id: 'shareable_moment',
      label: 'Describe a shareable musical moment, or the piece you will prepare first.',
      kind: 'textarea',
    },
    {
      id: 'sharing_method',
      label: 'Which simple way of sharing would you genuinely use?',
      kind: 'select',
      options: [
        { id: 'PHONE_VIDEO', label: 'A phone video' },
        { id: 'AUDIO_POST', label: 'An audio post over artwork' },
        { id: 'IN_PERSON', label: 'Playing it to people in person' },
        { id: 'SEND_DIRECTLY', label: 'Sending it directly to a few people' },
      ],
    },
    {
      id: 'blocker',
      label: 'Optional: what is making it difficult to begin?',
      kind: 'textarea',
      optional: true,
    },
  ],
};
